// SEO Configuration for different pages
export interface PageSEO {
  title: string;
  description: string;
  keywords: string;
  image?: string;
}

const seoConfig: Record<string, PageSEO> = {
  home: {
    title: 'Kingdom Studio - AI Video Creation Platform for Christian Creators',
    description: 'Create professional videos in 30 minutes with AI. Complete platform for Christian content creators, churches, and ministries. Replace 8+ tools with one solution. Save $150+/month.',
    keywords: 'AI movie, AI film, AI mini movie, AI business tools, AI tools, AI ads, AI marketing, AI video creator, Christian video maker, church video production, ministry content creation, AI video editing, faith-based video tools',
  },
  
  features: {
    title: 'Features - All-in-One AI Video Creation Tools',
    description: 'Discover Kingdom Studio\'s complete AI toolkit: Script Generator, Video Creation, Captions, Thumbnails, Audio Generation, Title & Description Writer, and Social Media Publishing. Everything you need in one platform.',
    keywords: 'AI movie, AI film, AI business tools, AI tools, AI thumbnail, AI audio, AI voice, AI script, AI animation production, AI brand storytelling, AI video features, video editing tools, AI captions',
  },
  
  pricing: {
    title: 'Pricing Plans - Start Creating for $19/month',
    description: 'Affordable AI video creation starting at $19/month. Replace $200+ of separate tools with one integrated platform. Minister, Ministry, and Pro plans available. Save thousands annually.',
    keywords: 'AI movie pricing, AI film tools cost, AI business tools pricing, AI tools subscription, AI ads creator pricing, AI marketing tools cost, video creator pricing, affordable video tools',
  },
  
  about: {
    title: 'About Us - Empowering Voices Through AI Innovation',
    description: 'Kingdom Studio is a faith-based AI company on a mission to help everyone share their story. We provide powerful AI tools that make professional content creation accessible to all, regardless of technical skill or budget.',
    keywords: 'AI movie company, AI film production company, AI business tools provider, AI marketing platform, Christian AI company, faith-based technology, AI content creation company',
  },
  
  scriptGenerator: {
    title: 'AI Script Generator - Create Video Scripts in Minutes',
    description: 'Generate professional video scripts with AI. Perfect for sermons, devotionals, teachings, and Christian content. Auto-convert scripts to optimized video prompts with GPT and Claude AI.',
    keywords: 'AI script writer, AI movie script, AI film script generator, AI animation script, AI brand storytelling script, video script generator, AI content writer, scriptwriting tool',
  },
  
  captionGenerator: {
    title: 'AI Caption Generator - Captions & Text Overlay for Videos',
    description: 'Add professional captions and title text overlays to your videos with AI-powered speech-to-text. Generate captions, add custom text titles, 15+ styles, timeline editing, and export formats (SRT, VTT, MP4).',
    keywords: 'AI captions, AI movie captions, AI film subtitles, video text overlay, video title generator, AI text on video, video subtitles, speech to text, automatic captions, subtitle generator',
  },
  
  thumbnailGenerator: {
    title: 'AI Thumbnail Creator - Eye-Catching Video Thumbnails',
    description: 'Create stunning video thumbnails with AI. Professional design tools, drag-and-drop editor, 15+ fonts, custom assets, and AI-powered image generation. Perfect for YouTube, Instagram, and TikTok.',
    keywords: 'AI thumbnail, AI thumbnail generator, AI thumbnail creator, YouTube thumbnail maker, video thumbnail design, thumbnail editor, custom thumbnails',
  },
  
  audioGenerator: {
    title: 'AI Audio Generator - Voice Narration & Music',
    description: 'Generate professional voice narration and background music with AI. ElevenLabs-quality voice synthesis, custom soundtracks, and sound effects. Perfect for video voiceovers and audio content.',
    keywords: 'AI audio, AI voice, AI voice generator, text to speech, AI music generator, voiceover creator, audio narration, sound effects generator, AI audio production',
  },
  
  soundGenerator: {
    title: 'AI Soundtrack & Sound Effects Generator',
    description: 'Create custom soundtracks and sound effects for your videos with AI. Professional audio mixing, melody generation, soundtrack creation, and effects library. Enhance your videos with perfect audio.',
    keywords: 'AI audio, AI voice, AI music generator, AI soundtrack, AI sound effects, audio mixer, custom music, video soundtrack, background music generator, sound effects library',
  },
  
  titleGenerator: {
    title: 'AI Title, Description, Hashtag & Timeline Generator',
    description: 'Generate engaging titles, descriptions, hashtags, and social media timelines with AI. Optimized for YouTube, Instagram, TikTok. Boost discoverability and engagement with SEO-optimized content and posting schedules.',
    keywords: 'AI marketing, AI ads, video title generator, hashtag generator, social media timeline, YouTube description writer, SEO video titles, social media captions, video metadata, AI copywriter',
  },
  
  socialMedia: {
    title: 'Social Media Publisher - Share to YouTube, Instagram, TikTok',
    description: 'Publish your videos directly to YouTube, Instagram, TikTok, and more from one dashboard. Multi-account management, scheduled posting, automated publishing, and analytics for all your social platforms.',
    keywords: 'AI marketing, AI ads, social media scheduler, YouTube publisher, Instagram scheduler, TikTok uploader, multi-platform posting, social media management, content scheduler',
  },
  
  promptGenerator: {
    title: 'AI Prompt Generator - Transform Scripts into Video Prompts',
    description: 'Convert your scripts into optimized AI video prompts. Interactive AI assistant guides you through prompt creation for perfect video generation. Works with all major AI video platforms.',
    keywords: 'AI movie prompts, AI film prompts, AI prompt generator, video prompt creator, script to prompt, AI video prompts, prompt engineering',
  },
  
  createContent: {
    title: 'AI Content Studio - Transform Audio into Video',
    description: 'Transform your audio into engaging video content with AI. Upload audio files and create professional videos with captions, effects, and optimized formatting in 9:16 and 16:9 formats.',
    keywords: 'AI movie maker, AI film production, AI mini movie creator, AI audio to video, audio video creator, 9:16 video, 16:9 video, AI animation production, AI brand storytelling, video compilation',
  },
  
  aiVideoEditor: {
    title: 'AI Video Editor - Edit Videos Without Audio',
    description: 'Edit and enhance your videos without audio using our AI-powered video editor. Professional video editing with captions, effects, and optimized formatting. Perfect for silent videos and visual content.',
    keywords: 'AI video editor, video editing, no audio video, silent video editor, video processor, AI video editing, professional video editor, video enhancement, visual content editor',
  },
  
  dashboard: {
    title: 'Dashboard - Your Video Projects & Analytics',
    description: 'Manage all your video projects in one place. Track usage, view analytics, organize content, and access all your created videos. Your command center for AI video creation.',
    keywords: 'video dashboard, project management, content analytics, video library, project organizer, usage tracking',
  },
  
  settings: {
    title: 'Settings - Account & Preferences',
    description: 'Customize your Kingdom Studio experience. Manage account settings, billing, preferences, and integrations. Control your AI video creation platform.',
    keywords: 'account settings, user preferences, billing management, platform settings, account customization',
  },
  
  usage: {
    title: 'Usage & Credits - Track Your Platform Usage',
    description: 'Monitor your credit usage, generation history, and platform statistics. View detailed breakdowns of features used, videos created, and remaining credits.',
    keywords: 'usage tracking, credit balance, video statistics, platform analytics, usage history, credit management',
  },
};

export default seoConfig;
